import { useLoading } from "@/Contexts/LoadingContext";
import { useAtom } from "jotai/index";
import { usePage } from "@inertiajs/inertia-react";
import React, { useEffect, useRef, useState } from "react";
import { Inertia } from "@inertiajs/inertia";
import { QUESTION_TYPES } from "@/Enums/question_types";
import { Tag } from "@/Components/Tag";
import {
  LabeledInput,
  LabeledMultiSelectAutocomplete,
} from "@/Components/Form";
import { Search } from "react-feather";
import ShrinkableDashboardFilterItems from "@/Components/ShrinkableDashboardFilterItems";
import { AccordionItem, Dialog } from "@/Components";

import { isActivitiesLibraryOpenAtom } from "@/Pages/Reporting/Owner/Index";
import { LoaderCircle } from "@/Icons";
import ResultTypeIcon from "@/Components/Questions/ResultTypeIcon";

function ChooseFromLibrarySidebar() {
  const { isLoaded, setIsLoaded } = useLoading();

  const [openModal, setOpenModal] = useAtom(isActivitiesLibraryOpenAtom);

  const { props } = usePage();

  const team = props?.team?.data || {};
  const tagsData = props?.tags?.data || [];

  const [libraryQuestions, setLibraryQuestions] = useState([]);
  const [undoableQuestions, setUndoableQuestions] = useState([]);

  // on mount, reload only library_questions
  useEffect(() => {
    if (openModal) {
      Inertia.reload({
        only: ["library_questions"],
      });
    }
  }, [openModal]);

  useEffect(() => {
    if (
      (!isLoaded ||
        libraryQuestions.length !== props?.library_questions?.data?.length ||
        0) &&
      props?.library_questions?.data
    ) {
      setLibraryQuestions(props.library_questions.data);
      setIsLoaded(true);
    }
  }, [props.library_questions]);

  const tagOptions = tagsData.map((tag) => {
    return {
      value: String(tag.id),
      label: tag.name,
    };
  });

  tagOptions.sort((a, b) => a.label.localeCompare(b.label));

  tagOptions.push({ value: "other", label: "Other" });

  const typeOptions = [
    {
      value: QUESTION_TYPES.Calculate,
      label: QUESTION_TYPES.toString(QUESTION_TYPES.Calculate),
    },
    {
      value: QUESTION_TYPES.Distribute,
      label: QUESTION_TYPES.toString(QUESTION_TYPES.Distribute),
    },
    {
      value: QUESTION_TYPES.FreeText,
      label: QUESTION_TYPES.toString(QUESTION_TYPES.FreeText),
    },
    {
      value: QUESTION_TYPES.Select,
      label: QUESTION_TYPES.toString(QUESTION_TYPES.Select),
    },
  ];

  const [search, setSearch] = useState("");
  const [tags, setTags] = useState(tagOptions.map((t) => t.value));
  const [types, setTypes] = useState(typeOptions.map((t) => t.value));

  const ref = useRef(null);

  const filteredQuestions = libraryQuestions.filter((question) => {
    const hasTags = tags.some(
      (tag) =>
        question.tags.some((t) => t.id === parseInt(tag)) ||
        (tag === "other" && question.tags.length === 0),
    );

    const hasTypes = types.includes(question.type.enum);

    let isInSearch = true;
    if (search) {
      const keywords = search.split(" ");

      isInSearch = keywords.every((keyword) =>
        question.title.toLowerCase().includes(keyword),
      );
    }

    const isUndoable = undoableQuestions.includes(question.id);
    const isAttached = team.questions.some((tq) => tq.id === question.id);

    return isInSearch && hasTags && hasTypes && (!isAttached || isUndoable);
  });

  function AccordionTitle({ question }) {
    return (
      <div>
        <h3 className="text-base font-bold">{question.title}</h3>

        <div className="mt-2">
          {question.tags.map((tag, i) => (
            <Tag key={i} margin="mr-2">
              {tag.name}
            </Tag>
          ))}
        </div>
      </div>
    );
  }

  function AccordionAction({
    question,
    team,
    className = "text-blue underline hover:no-underline",
  }) {
    const handleAttachQuestion = (question) => {
      return new Promise((resolve, reject) => {
        Inertia.post(
          route("team.question.attach", { question: question, team: team.id }),
          null,
          {
            onSuccess: () => {
              setUndoableQuestions((prev) => [...prev, question]);
              resolve();
            },
            onError: (error) => {
              reject(error);
            },
          },
        );
      });
    };

    const handleDetachQuestion = (question) => {
      return new Promise((resolve, reject) => {
        Inertia.delete(
          route("team.question.detach", {
            question: question,
            team: team.id,
          }),
          {
            onSuccess: () => {
              setUndoableQuestions((prev) =>
                prev.filter((q) => q !== question),
              );
              resolve();
            },
            onError: (error) => {
              reject(error);
            },
          },
        );
      });
    };

    const isAttached = team.questions.some((tq) => tq.id === question.id);

    const [isItemLoading, setIsItemLoading] = useState(false);

    const handleClick = async (e) => {
      e.preventDefault();
      e.stopPropagation();

      setIsItemLoading(true);

      if (isAttached) {
        handleDetachQuestion(question.id).then(() => {
          setIsItemLoading(false);
        });
      } else {
        handleAttachQuestion(question.id).then(() => {
          setIsItemLoading(false);
        });
      }
    };

    return (
      <button
        className={`flex w-28 justify-center ${className}`}
        disabled={isItemLoading}
        onClick={handleClick}
      >
        {isItemLoading && <LoaderCircle size={18} className="mt-1" />}
        {!isItemLoading && <>{isAttached ? "Undo" : "Add activity"}</>}
      </button>
    );
  }

  function MassAction({ team }) {
    const ids = filteredQuestions
      .filter((q) => !team.questions.some((tq) => tq.id === q.id))
      .map((q) => q.id);

    const [isItemLoading, setIsItemLoading] = useState(false);

    if (!filteredQuestions.length) {
      return null;
    }

    const handleAttachQuestions = () => {
      return new Promise((resolve, reject) => {
        Inertia.post(
          route("team.question.multi.attach", {
            question_ids: ids,
            team: team.id,
          }),
          null,
          {
            onSuccess: () => {
              setUndoableQuestions((prev) => [...prev, ...ids]);
              resolve();
            },
            onError: (error) => {
              reject(error);
            },
          },
        );
      });
    };

    const handleDetachQuestions = () => {
      return new Promise((resolve, reject) => {
        const ids = filteredQuestions.map((q) => q.id);

        Inertia.delete(
          route("team.question.multi.detach", {
            question_ids: ids,
            team: team.id,
          }),
          null,
          {
            onSuccess: () => {
              setUndoableQuestions((prev) =>
                prev.filter((q) => !ids.includes(q)),
              );
              resolve();
            },
            onError: (error) => {
              reject(error);
            },
          },
        );
      });
    };

    const handleClick = async () => {
      setIsItemLoading(true);

      if (ids.length > 0) {
        handleAttachQuestions().then(() => {
          setIsItemLoading(false);
        });
      } else {
        handleDetachQuestions().then(() => {
          setIsItemLoading(false);
        });
      }
    };

    return (
      <button
        className={`btn btn-blue relative flex w-40 shrink-0 grow-0 items-center justify-center px-3 py-3`}
        disabled={isItemLoading}
        onClick={handleClick}
      >
        {isItemLoading && <LoaderCircle size={18} />}
        {!isItemLoading && (
          <>{ids.length > 0 ? "Add all activities" : "Undo"}</>
        )}
      </button>
    );
  }

  function QuestionDetails({ question, team, setIsActive }) {
    return (
      <>
        <div className="flex flex-col gap-2">
          <p>
            <span className="font-bold">Question </span>
            {question.question_text || question.inputs[0].label}
          </p>
          <p>
            <span className="font-bold">Activity type </span>
            {QUESTION_TYPES.toString(question.type.enum)}
          </p>
          <p className="flex">
            <span className="font-bold"> Data displayed in results</span>
            <ResultTypeIcon resultType={question?.result_type} />
            &nbsp;
            {question?.result_type?.charAt(0).toUpperCase() +
              question?.result_type?.slice(1)}
          </p>
          {question.tags.length > 0 &&
            question.tags?.some((t) =>
              t.budgetQuestions?.find((q) => q.id === question.id),
            ) && (
              <p>
                <span className="font-bold">Budget question </span>
                {
                  question.tags.find((t) =>
                    t.budgetQuestions?.find((q) => q.id === question.id),
                  ).name
                }
              </p>
            )}

          {question?.inputs?.some((i) => i?.emission_factor) && (
            <p>
              <span className="font-bold">Emission factors </span>
              {question.inputs
                .map(
                  (input, index) =>
                    `${input.label} ${input.emission_factor.name} (KG per ${input.emission_factor.unit})`,
                )
                .join(", ")}
            </p>
          )}
        </div>

        <div className="mt-4 flex justify-end gap-4">
          <button
            className="text-blue underline hover:no-underline"
            onClick={() => setIsActive(false)}
          >
            Close
          </button>
          <AccordionAction
            question={question}
            team={team}
            className="btn btn-blue grow-0 px-2 py-3"
          />
        </div>
      </>
    );
  }

  if (!openModal) return null;

  return (
    <Dialog
      openModal={openModal}
      closeModal={() => setOpenModal(false)}
      title="Add activities from library"
    >
      <div
        className="flex w-full flex-col flex-wrap gap-4 sm:flex-row"
        ref={ref}
      >
        <LabeledInput
          value={search}
          type="search"
          placeholder="Search"
          containerClass="w-full"
          unit={search === "" ? <Search className="relative" /> : null}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
        />

        <LabeledMultiSelectAutocomplete
          title="Categories"
          className="w-full sm:basis-1/2-gap-4"
          placeholder="Categories"
          options={tagOptions}
          defaultValue={tags || []}
          onSelect={(val) => {
            const values = val.map((v) => v.value);
            setTags(values);
          }}
        />

        <LabeledMultiSelectAutocomplete
          className="w-full sm:basis-1/2-gap-4"
          title="Activity types"
          placeholder="Types"
          options={typeOptions}
          defaultValue={types || []}
          onSelect={(val) => {
            const values = val.map((v) => v.value);
            setTypes(values);
          }}
        />
      </div>

      {!isLoaded && (
        <div className="flex items-center justify-center">
          <LoaderCircle size={40} />
        </div>
      )}
      {isLoaded && libraryQuestions.length === 0 && (
        <div className="flex items-center justify-center p-8 text-center">
          <h3 className="mx-4 text-lg font-bold text-blue-dark">
            No activities found in the library
          </h3>
        </div>
      )}

      {isLoaded && libraryQuestions.length > 0 && (
        <div className="flex w-full flex-col">
          <div className="mb-4 flex justify-between gap-4">
            <div className="w-1/2 overflow-hidden">
              <p className="text-md font-bold">
                {filteredQuestions.length} results
              </p>

              <div className="flex gap-2 max-sm:hidden">
                <p>For:</p>
                <ShrinkableDashboardFilterItems
                  gap="gap-2"
                  items={[
                    ...tagOptions
                      .filter((t) => tags.includes(t.value))
                      .map((t) => ({
                        name: t.label,
                        html: <span>{t.label},</span>,
                      })),
                    ...typeOptions
                      .filter((t) => types.includes(t.value))
                      .map((t) => ({
                        name: t.label,
                        html: <span>{t.label},</span>,
                      })),
                  ]}
                />
              </div>
            </div>
            <MassAction team={team} />
          </div>
          {filteredQuestions.map((question) => (
            <AccordionItem
              key={question.id}
              title={<AccordionTitle question={question} />}
              titleStyle="text-base font-bold"
              className="border"
              titleright={<AccordionAction question={question} team={team} />}
            >
              <QuestionDetails question={question} team={team} />
            </AccordionItem>
          ))}

          {filteredQuestions.length > 8 && (
            <button
              className="btn btn-blue my-4 grow-0 self-center py-2"
              onClick={() => {
                ref.current.scrollIntoView({ behavior: "smooth" });
              }}
            >
              To the top
            </button>
          )}
        </div>
      )}
    </Dialog>
  );
}

export default ChooseFromLibrarySidebar;
